export interface SectionHrefRemapOptions {
  /**
   * The legacy product URL segment, without slashes — e.g. `"crm"`,
   * `"hiring"`, `"document"`, `"lens"`. Matches `/<prefix>` and `/<prefix>/…`.
   */
  prefix: string;
  /**
   * Where the bare `/<prefix>` root href lands, relative to the section
   * basePath — e.g. `"overview"` (crm/hiring), `"docs"` (document),
   * `"lens"` (lens). Result: `${basePath}/${root}`.
   */
  root: string;
  /**
   * Optional namespace prepended to sub-paths under the section basePath.
   * `""` for crm/hiring (`/crm/leads` → `${basePath}/leads`); `"docs"` for
   * document (`/document/targets` → `${basePath}/docs/targets`); `"lens"` for
   * lens. Default `""`.
   */
  subPath?: string;
}

/**
 * Builds the `(href, basePath) => string` remapper that rewrites a legacy
 * standalone `/<prefix>/…` href to a project-section base path, leaving the
 * API namespace (`/<prefix>/api/…`) and already-absolute external links
 * alone. Replaces the four hand-copied `remap<Product>Href` functions
 * (`_crm/remap.ts`, `_hiring/remap.ts`, `_document/remap.ts`,
 * `_lens/remap.ts`), each of which carried a comment saying it "mirrors" the
 * others.
 */
export function makeSectionHrefRemapper({
  prefix,
  root,
  subPath = "",
}: SectionHrefRemapOptions): (href: string, basePath: string) => string {
  const apiNs = `/${prefix}/api`;
  const exact = `/${prefix}`;
  const childNs = `/${prefix}/`;
  const sub = subPath ? `/${subPath}` : "";
  return (href: string, basePath: string): string => {
    if (!href) return href;
    if (href.startsWith(apiNs)) return href;
    if (href === exact) return `${basePath}/${root}`;
    if (href.startsWith(childNs)) {
      return `${basePath}${sub}/${href.slice(childNs.length)}`;
    }
    return href;
  };
}
